/**
 * PLATFORM-DESIGN-SYSTEM-CODIFICATION-V1 -- S1 OPENING LINE (the first of
 * the five AdoraCare signatures; owner-home/SYSTEM-EXTRACT.md S1, first
 * composed carrier composed-owner-home.html).
 *
 * Every role home opens with a one-to-three clause story in plain
 * language, ABOVE its hero figure, answering the surface's ONE QUESTION
 * before any number is read. DETERMINISTIC BY CONTRACT: composition is
 * pure rules over already-served figures (owner C-3) -- the PAGE composes
 * the clauses; this primitive only renders them. Every clause must map
 * 1:1 to a figure visible on the page (census check C-1 applies to
 * clauses); no free adjectives; never generative.
 *
 * State colour rides the EXISTING tone tokens only (C-10: no second
 * palette): healthy = positive, attention = danger, quiet = neutral
 * inset, scale/fiction = info -- with the LED dot. On fiction/scale
 * frames the line itself carries the DESIGN-ONLY tag (the caller passes
 * the tag node; canonical label: ui/lexicon.ts DESIGN_ONLY_TAG_LABEL).
 *
 * PRESENTATION-ONLY: no fetching, no derivation, no business logic.
 */

import type { ReactNode } from 'react';
import './signatures.css';

/** The fixed S1 state vocabulary (extract: healthy / needs attention /
 *  quiet; ahead/behind ride clause TEXT only, never a fourth state). */
export type OpeningLineState = 'healthy' | 'attention' | 'quiet';

/** S1 draws 1-3 clauses (owner C-3: "clause slots, composed in order,
 *  1-3 clauses drawn"). */
export const MAX_OPENING_LINE_CLAUSES = 3;

/** THE pure state -> tone map (C-10; pinned in tests). `designOnly`
 *  (fiction/scale frames) overrides to info. */
export function openingLineTone(
  state: OpeningLineState,
  designOnly = false,
): 'positive' | 'danger' | 'neutral' | 'info' {
  if (designOnly) return 'info';
  if (state === 'healthy') return 'positive';
  if (state === 'attention') return 'danger';
  return 'neutral';
}

export interface OpeningLineProps {
  readonly state: OpeningLineState;
  /** 1-3 composed clauses, each a complete sentence already proven by a
   *  rendered figure. Entries beyond the third are NOT rendered. */
  readonly clauses: ReadonlyArray<string>;
  /** Fiction/scale frame marker: tones the line info and expects `tag`. */
  readonly designOnly?: boolean;
  /** The body-governing tag (e.g. the DESIGN-ONLY chip) rendered on the
   *  line itself. */
  readonly tag?: ReactNode;
  readonly testId?: string;
}

export default function OpeningLine({
  state,
  clauses,
  designOnly = false,
  tag,
  testId,
}: OpeningLineProps): JSX.Element {
  const tone = openingLineTone(state, designOnly);
  const drawn = clauses
    .filter((clause) => clause.trim().length > 0)
    .slice(0, MAX_OPENING_LINE_CLAUSES);
  return (
    <section
      className={`ac-openline ac-openline--${tone}`}
      data-state={state}
      data-testid={testId}
    >
      <span className="ac-openline__led" aria-hidden="true" />
      <p className="ac-openline__text">{drawn.join(' ')}</p>
      {tag ?? null}
    </section>
  );
}
